import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction, asyncAction } from "@client/lib/actions";
import { parseQueryString } from "@client/lib/api";
import { type AppState, type BaseAppState, type EntityRef, Status } from "@client/types";
import toasts from "@common/lib/toast";
import { type AnyAction, type Dispatch } from "redux";
import { type OrderConfirmation } from "../OrderConfirmation/types";
import { initAction } from "../actions";
import api from "./api";
import { localStateSelector } from "./selectors";
import { type OrderDetail } from "./types";

export enum Actions {
  InitBegin = "ORDER_DETAIL_INIT_BEGIN",
  InitFailure = "ORDER_DETAIL_INIT_FAILURE",
  InitSuccess = "ORDER_DETAIL_INIT_SUCCESS",

  DownloadInvoiceBegin = "MODULE_ORDER_CONFIRMATION_DOWNLOAD_INVOICE_BEGIN",
  DownloadInvoiceFailure = "MODULE_ORDER_CONFIRMATION_DOWNLOAD_INVOICE_FAILURE",
  DownloadInvoiceSuccess = "MODULE_ORDER_CONFIRMATION_DOWNLOAD_INVOICE_SUCCESS",

  CreateCommentBegin = "ORDER_DETAIL_CREATE_COMMENT_BEGIN",
  CreateCommentFailure = "ORDER_DETAIL_CREATE_COMMENT_FAILURE",
  CreateCommentSuccess = "ORDER_DETAIL_CREATE_COMMENT_SUCCESS",

  SetAddCommentModalVisible = "ORDER_DETAIL_ADD_COMMENT_MODAL"
}

export type InitBeginActionData = EntityRef & {
  orderId: string;
};
export type InitSuccessActionData = EntityRef & OrderDetail.Api.InitResponse;
export const _init = initAction<BaseAppState, InitBeginActionData, OrderDetail.Api.InitResponse, InitSuccessActionData>(
  {
    types: {
      begin: Actions.InitBegin,
      failure: Actions.InitFailure,
      success: Actions.InitSuccess
    },
    action: (state: BaseAppState, dispatch: any, params: InitBeginActionData) => {
      const apiInfo = makeModuleApiInfoSelector()(state, params);
      return api.init(apiInfo, params);
    },
    getShouldInit: (state: AppState, params: InitBeginActionData) => !!params.orderId
  }
);

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  const orderId = parseQueryString().o;

  if (localState.status === Status.None) {
    dispatch(_init({ ...args, orderId: orderId! }));
  }
};

export type DownloadInvoiceBeginActionData = EntityRef & {};
export const _downloadInvoice = asyncAction<AppState, DownloadInvoiceBeginActionData, void>({
  types: {
    begin: Actions.DownloadInvoiceBegin,
    failure: Actions.DownloadInvoiceFailure,
    success: Actions.DownloadInvoiceSuccess
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: DownloadInvoiceBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.downloadInvoice(apiInfo, { portalId: state.portal.id, orderId: parseQueryString().o });
  }
});

export type DownloadInvoiceArgs = EntityRef;
export const downloadInvoice = (args: DownloadInvoiceArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status !== "Downloading") {
    dispatch(_downloadInvoice(args));
  }
};

export type CreateCommentBeginActionData = EntityRef & {
  comment: string;
};
export type CreateCommentSuccessActionData = OrderConfirmation.Api.CreateCommentResponse;
export const _createComment = apiAction<AppState, CreateCommentBeginActionData, CreateCommentSuccessActionData>({
  types: {
    begin: Actions.CreateCommentBegin,
    failure: Actions.CreateCommentFailure,
    success: [
      Actions.CreateCommentSuccess,
      ({ dispatch, params }) => dispatch(hideAddCommentModal(params)),
      () => {
        toasts.submitted();
      }
    ]
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: CreateCommentBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const localState = localStateSelector(state, args);

    return api.createComment(apiInfo, localState.orderSummary!.order.orderId, args.comment);
  }
});

export type CreateCommentArgs = EntityRef & {
  comment: string;
};
export const createComment = (args: CreateCommentArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status !== "Creating") {
    dispatch(_createComment(args));
  }
};

export type SetAddCommentModalVisibleActionData = EntityRef & {
  isVisible: boolean;
};
export const hideAddCommentModal = (ref: EntityRef) => ({
  type: Actions.SetAddCommentModalVisible,
  data: <SetAddCommentModalVisibleActionData>{ ...ref, isVisible: false }
});
export const showAddCommentModal = (ref: EntityRef) => ({
  type: Actions.SetAddCommentModalVisible,
  data: <SetAddCommentModalVisibleActionData>{ ...ref, isVisible: true }
});
